import { useEffect, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/auth/useAuth';

import { InstallPromptManager } from './InstallPromptManager';
import { OfflineBanner } from './OfflineBanner';
import { RefusedAnswersNotice } from './RefusedAnswersNotice';
import { installPwaCacheDebug } from './cacheDebug';
import {
  installManualReplayListeners,
  installServiceWorkerMessageBridge,
} from './registerServiceWorker';
import { listenToSyncReports } from './syncMessages';
import { AppUpdateContext, useAppUpdates } from './useAppUpdates';

interface PwaShellProps {
  children: ReactNode;
}

export function PwaShell({ children }: PwaShellProps) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const { newBuild } = useAppUpdates();
  const isSignedIn = !auth.loading && Boolean(auth.user);

  useEffect(() => installServiceWorkerMessageBridge(auth), [auth]);
  useEffect(
    () => (isSignedIn ? installManualReplayListeners() : undefined),
    [isSignedIn],
  );
  useEffect(() => {
    installPwaCacheDebug();
  }, []);
  useEffect(
    () =>
      listenToSyncReports((report) => {
        if (report.type === 'sync-status' && report.state === 'synced') {
          void queryClient.invalidateQueries({ queryKey: ['component'] });
        }
      }),
    [queryClient],
  );

  // There is no update prompt (issue #66): a new build loads at the next page
  // change, through LoadNewVersionOnPageChange inside the router.
  return (
    <AppUpdateContext.Provider value={newBuild}>
      <OfflineBanner />
      <RefusedAnswersNotice />
      <InstallPromptManager />
      {children}
    </AppUpdateContext.Provider>
  );
}
